import React from "react";
import { Award, CheckCircle } from "lucide-react";

// Shared building blocks for candidate profile data — used by both the public
// ProfessionalProfilePage (full hero shell) and the employer-facing
// EmployerCandidateView (compact inline shell). Keeping this here means both
// views render the exact same real data/sections, never two divergent copies.

const PORTAL_META = {
  jobs:   { name: "InfoCityJobs",   color: "#028CF8", emoji: "💼", desc: "Job opportunities" },
  prop:   { name: "InfoCityProp",   color: "#028CF8", emoji: "🏠", desc: "Real estate roles" },
  cars:   { name: "InfoCityCars",   color: "#028CF8", emoji: "🚗", desc: "Automotive sector" },
  boat:   { name: "InfoCityBoat",   color: "#028CF8", emoji: "⛵", desc: "Marine industry" },
  travel: { name: "InfoCityTravel", color: "#028CF8", emoji: "✈️", desc: "Travel & tourism" },
  shop:   { name: "InfoCityShop",   color: "#028CF8", emoji: "🛍️", desc: "Retail & e-commerce" },
};

/* ── Completeness Ring ────────────────────────────────────────────────────── */
export function CompletenessRing({ score, size = 80 }) {
  const radius = (size - 12) / 2;
  const circ   = 2 * Math.PI * radius;
  const fill   = (score / 100) * circ;
  const color  = score >= 80 ? "#10B981" : score >= 60 ? "#028CF8" : "#64748B";
  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#F1F5F9" strokeWidth={6} />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={6}
          strokeDasharray={`${fill} ${circ}`} strokeLinecap="round" />
      </svg>
      <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center" }}>
        <span style={{ fontSize: size < 70 ? "14px" : "18px", fontWeight: 700, color, fontFamily: "Poppins, sans-serif" }}>{score}</span>
        <span style={{ fontSize: "9px", color: "#94A3B8", fontFamily: "Poppins, sans-serif", lineHeight: 1 }}>Profile</span>
      </div>
    </div>
  );
}

/* ── Timeline item ────────────────────────────────────────────────────────── */
export function TimelineItem({ title, subtitle, dates, description, color = "#028CF8" }) {
  return (
    <div style={{ display: "flex", gap: "14px" }}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
        <div style={{ width: 10, height: 10, borderRadius: "50%", background: color, border: `2px solid ${color}`, marginTop: "4px" }} />
        <div style={{ flex: 1, width: 2, background: "#F1F5F9", marginTop: "4px" }} />
      </div>
      <div style={{ paddingBottom: "20px", minWidth: 0, flex: 1 }}>
        <p style={{ fontSize: "14px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", marginBottom: "2px" }}>{title}</p>
        {subtitle && <p style={{ fontSize: "13px", fontWeight: 600, color, fontFamily: "Poppins, sans-serif", marginBottom: "2px" }}>{subtitle}</p>}
        {dates && <p style={{ fontSize: "11px", color: "#94A3B8", fontFamily: "Poppins, sans-serif", marginBottom: "6px" }}>{dates}</p>}
        {description && <p style={{ fontSize: "13px", color: "#64748B", fontFamily: "Poppins, sans-serif", lineHeight: "1.5" }}>{description}</p>}
      </div>
    </div>
  );
}

/* ── Full section grid (Summary/Experience/Education + Skills/Languages/
   Certifications/Ecosystem) — the real, shared candidate data blocks.
   Callers own the surrounding container/shell (hero vs compact header). ── */
export function CandidateProfileContent({ profile, t }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

      {/* Main column */}
      <div className="lg:col-span-2 space-y-5">

        {/* Summary */}
        {profile.summary && (
          <section style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "20px", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#028CF8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "8px", fontFamily: "Poppins, sans-serif" }}>{t("profProfile.about")}</p>
            <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", marginBottom: "10px" }}>{t("profProfile.summary")}</h2>
            <p style={{ fontSize: "14px", color: "#64748B", lineHeight: "1.7", fontFamily: "Poppins, sans-serif" }}>{profile.summary}</p>
          </section>
        )}

        {/* Experience */}
        {profile.experience?.length > 0 && (
          <section style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "20px", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#028CF8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "8px", fontFamily: "Poppins, sans-serif" }}>{t("profProfile.career")}</p>
            <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", marginBottom: "18px" }}>{t("profProfile.workExp")}</h2>
            {profile.experience.map((exp, i) => (
              <TimelineItem
                key={i}
                title={exp.position || exp.company}
                subtitle={exp.position ? exp.company : null}
                dates={`${exp.start_date || ""} – ${exp.current ? t("profProfile.present") : exp.end_date || ""}`}
                description={exp.description}
                color="#028CF8"
              />
            ))}
          </section>
        )}

        {/* Education */}
        {profile.education?.length > 0 && (
          <section style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "20px", padding: "24px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#028CF8", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "8px", fontFamily: "Poppins, sans-serif" }}>{t("profProfile.academic")}</p>
            <h2 style={{ fontSize: "18px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", marginBottom: "18px" }}>{t("profProfile.education")}</h2>
            {profile.education.map((edu, i) => (
              <TimelineItem
                key={i}
                title={`${edu.degree || ""} ${edu.field ? `in ${edu.field}` : ""}`.trim() || edu.institution}
                subtitle={edu.institution}
                dates={edu.year}
                color="#028CF8"
              />
            ))}
          </section>
        )}
      </div>

      {/* Sidebar */}
      <div className="space-y-5">

        {/* Skills */}
        {profile.skills?.length > 0 && (
          <section style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "20px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "12px", fontFamily: "Poppins, sans-serif" }}>{t("profProfile.skills")}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
              {profile.skills.map((s) => (
                <span key={s} style={{ fontSize: "12px", fontWeight: 600, padding: "4px 11px", borderRadius: "999px", background: "#EFF6FF", color: "#028CF8", fontFamily: "Poppins, sans-serif" }}>{s}</span>
              ))}
            </div>
          </section>
        )}

        {/* Languages */}
        {profile.languages?.length > 0 && (
          <section style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "20px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "12px", fontFamily: "Poppins, sans-serif" }}>{t("profProfile.languages")}</p>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
              {profile.languages.map((l) => (
                <span key={l} style={{ fontSize: "12px", fontWeight: 600, padding: "4px 11px", borderRadius: "999px", background: "#F0FDF4", color: "#15803D", fontFamily: "Poppins, sans-serif" }}>{l}</span>
              ))}
            </div>
          </section>
        )}

        {/* Certifications */}
        {profile.certifications?.length > 0 && (
          <section style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "20px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "12px", fontFamily: "Poppins, sans-serif" }}>{t("profProfile.certifications")}</p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {profile.certifications.map((c, i) => {
                const label = typeof c === "string" ? c : c.name;
                const org = typeof c === "string" ? "" : c.issuer;
                return (
                  <li key={label + i} style={{ display: "flex", alignItems: "center", gap: "7px", fontSize: "13px", color: "#374151", fontFamily: "Poppins, sans-serif", marginBottom: "6px" }}>
                    <Award style={{ width: 13, height: 13, color: "#64748B", flexShrink: 0 }} />
                    {label}{org ? ` · ${org}` : ""}
                  </li>
                );
              })}
            </ul>
          </section>
        )}

        {/* InfoCity Ecosystem Relevance */}
        {profile.ecosystem_relevance?.length > 0 && (
          <section style={{ background: "white", border: "1px solid #E5E7EB", borderRadius: "20px", padding: "20px", boxShadow: "0 1px 3px rgba(0,0,0,0.05)" }}>
            <p style={{ fontSize: "11px", fontWeight: 700, color: "#64748B", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: "4px", fontFamily: "Poppins, sans-serif" }}>InfoCity Network</p>
            <p style={{ fontSize: "18px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", marginBottom: "14px" }}>{t("profProfile.portalCompat")}</p>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {profile.ecosystem_relevance.map((key) => {
                const meta = PORTAL_META[key];
                if (!meta) return null;
                return (
                  <div key={key} style={{ display: "flex", alignItems: "center", gap: "10px", padding: "10px 12px", background: "#F8FAFC", border: `1px solid ${key === "jobs" ? "#028CF8" : "#E5E7EB"}`, borderRadius: "12px" }}>
                    <span style={{ fontSize: "18px" }}>{meta.emoji}</span>
                    <div>
                      <p style={{ fontSize: "12px", fontWeight: 700, color: "#0F172A", fontFamily: "Poppins, sans-serif", lineHeight: 1.2 }}>{meta.name}</p>
                      <p style={{ fontSize: "11px", color: "#94A3B8", fontFamily: "Poppins, sans-serif" }}>{meta.desc}</p>
                    </div>
                    <CheckCircle style={{ width: 14, height: 14, color: "#028CF8", marginLeft: "auto", flexShrink: 0 }} />
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
